import { useState, useEffect, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;

const RegisterRestaurant = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const addressInputRef = useRef(null);
  const autocompleteRef = useRef(null);

  // Redirect if not authenticated or not a restaurant user
  useEffect(() => {
    if (!user) {
      navigate('/login');
      return;
    }
    if (user.role !== 'merchant') {
      navigate('/dashboard');
    }
  }, [user, navigate]);

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    address: '',
    phone: '',
    cuisine: '',
    location: {
      latitude: null,
      longitude: null
    }
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [myRestaurants, setMyRestaurants] = useState([]);
  const [loadingRestaurants, setLoadingRestaurants] = useState(true);
  const [mapsLoaded, setMapsLoaded] = useState(false);

  // Load Google Maps script
  useEffect(() => {
    if (!GOOGLE_MAPS_API_KEY || GOOGLE_MAPS_API_KEY === 'YOUR_GOOGLE_MAPS_API_KEY_HERE') {
      console.warn('Google Maps API key not configured');
      return;
    }

    // Check if script already loaded
    if (window.google && window.google.maps && window.google.maps.places) {
      setMapsLoaded(true);
      initAutocomplete();
      return;
    }

    // Load Google Maps script
    const script = document.createElement('script');
    script.src = `https://maps.googleapis.com/maps/api/js?key=${GOOGLE_MAPS_API_KEY}&libraries=places&region=IN`;
    script.async = true;
    script.defer = true;
    script.onload = () => {
      setMapsLoaded(true);
      initAutocomplete();
    };
    script.onerror = () => {
      console.error('Failed to load Google Maps script');
    };
    document.head.appendChild(script);

    return () => {
      // Cleanup if needed
    };
  }, []);

  // Initialize autocomplete
  const initAutocomplete = () => {
    if (!addressInputRef.current || !window.google || !window.google.maps) return;

    try {
      const autocomplete = new window.google.maps.places.Autocomplete(
        addressInputRef.current,
        {
          componentRestrictions: { country: 'in' }, // Restrict to India
          fields: ['formatted_address', 'geometry', 'name', 'address_components'],
          types: ['establishment', 'geocode'] // Allow businesses and addresses
        }
      );

      autocomplete.addListener('place_changed', () => {
        const place = autocomplete.getPlace();

        if (!place.geometry || !place.geometry.location) {
          console.error('No geometry data for selected place');
          return;
        }

        // Update form data with selected place
        setFormData(prev => ({
          ...prev,
          address: place.formatted_address || place.name,
          location: {
            latitude: place.geometry.location.lat(),
            longitude: place.geometry.location.lng()
          }
        }));
      });

      autocompleteRef.current = autocomplete;
    } catch (error) {
      console.error('Error initializing autocomplete:', error);
    }
  };

  useEffect(() => {
    fetchMyRestaurants();
  }, []);

  const fetchMyRestaurants = async () => {
    try {
      const response = await axios.get(`${API_URL}/restaurants/my/restaurants`);
      setMyRestaurants(response.data);
    } catch (error) {
      console.error('Error fetching restaurants:', error);
    } finally {
      setLoadingRestaurants(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    // Clear location if address is manually changed
    if (name === 'address' && autocompleteRef.current) {
      setFormData(prev => ({
        ...prev,
        location: {
          latitude: null,
          longitude: null
        }
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      // Prepare data to send
      const dataToSend = {
        name: formData.name,
        email: formData.email,
        address: formData.address,
        phone: formData.phone,
        cuisine: formData.cuisine
      };

      // Include location if available
      if (formData.location.latitude && formData.location.longitude) {
        dataToSend.location = {
          latitude: formData.location.latitude,
          longitude: formData.location.longitude
        };
      }

      await axios.post(`${API_URL}/restaurants/register`, dataToSend);

      // Reset form
      setFormData({
        name: '',
        email: '',
        address: '',
        phone: '',
        cuisine: '',
        location: {
          latitude: null,
          longitude: null
        }
      });

      fetchMyRestaurants();
      alert('Restaurant registered successfully!');
    } catch (error) {
      setError(error.response?.data?.message || 'Failed to register restaurant');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-8">My Restaurants</h1>

        <div className="bg-white rounded-lg shadow-lg p-8 mb-8">
          <h2 className="text-2xl font-semibold text-gray-900 mb-6">Register New Restaurant</h2>

          {error && (
            <div className="mb-6 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
                Restaurant Name *
              </label>
              <input
                type="text"
                id="name"
                name="name"
                required
                className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
                value={formData.name}
                onChange={handleChange}
              />
            </div>

            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                Email *
              </label>
              <input
                type="email"
                id="email"
                name="email"
                required
                className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
                value={formData.email}
                onChange={handleChange}
              />
            </div>

            <div>
              <label htmlFor="address" className="block text-sm font-medium text-gray-700 mb-2">
                Address * {mapsLoaded && <span className="text-xs text-gray-500">(Search with Google Maps)</span>}
              </label>
              <input
                ref={addressInputRef}
                type="text"
                id="address"
                name="address"
                required
                className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
                placeholder="Start typing your restaurant address..."
                value={formData.address}
                onChange={handleChange}
              />
              {formData.location.latitude && formData.location.longitude && (
                <p className="mt-1 text-xs text-green-600 flex items-center">
                  <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd"/>
                  </svg>
                  Location captured: {formData.location.latitude.toFixed(4)}, {formData.location.longitude.toFixed(4)}
                </p>
              )}
            </div>

            <div>
              <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-2">
                Phone *
              </label>
              <input
                type="tel"
                id="phone"
                name="phone"
                required
                className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
                value={formData.phone}
                onChange={handleChange}
              />
            </div>

            <div>
              <label htmlFor="cuisine" className="block text-sm font-medium text-gray-700 mb-2">
                Cuisine Type
              </label>
              <input
                type="text"
                id="cuisine"
                name="cuisine"
                className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
                placeholder="e.g., Italian, Mexican, Asian"
                value={formData.cuisine}
                onChange={handleChange}
              />
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full bg-primary-600 text-white py-3 px-4 rounded-md font-semibold hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50"
            >
              {loading ? 'Registering...' : 'Register Restaurant'}
            </button>
          </form>
        </div>

        {loadingRestaurants ? (
          <div className="text-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto"></div>
          </div>
        ) : myRestaurants.length > 0 ? (
          <div className="bg-white rounded-lg shadow-lg overflow-hidden">
            <div className="p-6 border-b border-gray-200">
              <h2 className="text-xl font-semibold text-gray-900">My Registered Restaurants</h2>
            </div>
            <div className="divide-y divide-gray-200">
              {myRestaurants.map((restaurant) => (
                <div key={restaurant._id} className="p-6 hover:bg-gray-50">
                  <div className="flex justify-between items-start">
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900">{restaurant.name}</h3>
                      <p className="text-sm text-gray-600 mt-1">{restaurant.address}</p>
                      {restaurant.cuisine && (
                        <span className="inline-block mt-2 px-3 py-1 bg-primary-100 text-primary-800 text-xs font-semibold rounded-full">
                          {restaurant.cuisine}
                        </span>
                      )}
                      {restaurant.location?.latitude && (
                        <p className="text-xs text-gray-500 mt-1">
                          📍 GPS: {restaurant.location.latitude.toFixed(4)}, {restaurant.location.longitude.toFixed(4)}
                        </p>
                      )}
                    </div>
                    <Link
                      to="/merchant/dashboard"
                      className="ml-4 bg-primary-600 text-white px-4 py-2 rounded-md text-sm font-semibold hover:bg-primary-700"
                    >
                      View Dashboard
                    </Link>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ) : (
          <div className="bg-white rounded-lg shadow p-8 text-center">
            <p className="text-gray-600">No restaurants registered yet.</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default RegisterRestaurant;